"use client";

import React from "react";
import { motion } from "framer-motion";
import { Shield, Zap, Globe, Lock, Server, CircleCheck as CheckCircle2, ArrowRight, Crosshair, Activity, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const providers = [{
  name: "NordVPN",
  logoIcon: Shield,
  badge: "Best for all-around use",
  description: "Balances speed, privacy, and ease of use better than anyone else. Its apps are stable, its encryption is strong, and it includes extras like a password manager and file locker. Suitable for nearly every type of user.",
  features: ["5400+ Servers", "WireGuard Support", "Strict No-Logs"],
  link: "https://vpngenix.com/go/NordVPN"
}, {
  name: "ExpressVPN",
  logoIcon: Zap,
  badge: "Fastest setup & app",
  description: "Known for keeping things simple. Install it, click connect, and it just works. No tweaking needed. It's a strong pick for new users and frequent travelers who need reliable access.",
  features: ["Lightway Protocol", "94 Countries", "Threat Manager"],
  link: "https://vpngenix.com/go/ExpressVPN"
}, {
  name: "Surfshark",
  logoIcon: Globe,
  badge: "Best value for multi-device",
  description: "One account. Unlimited devices. Affordable and surprisingly fast, even with multiple devices connected. It also includes an ad blocker and a clean, intuitive interface.",
  features: ["Unlimited Devices", "CleanWeb Adblock", "MultiHop"],
  link: "https://vpngenix.com/go/SurfShark"
}, {
  name: "CyberGhost",
  logoIcon: Activity,
  badge: "Easiest for streaming",
  description: "Offers servers labeled specifically for streaming, gaming, and torrenting. You don't have to guess which one to pick. Excellent choice if you're new to VPNs.",
  features: ["Streaming Servers", "45-Day Money Back", "NoSpy Servers"],
  link: "https://vpngenix.com/go/CyberGhost"
}, {
  name: "Proton VPN",
  logoIcon: Lock,
  badge: "Best for privacy-first",
  description: "Built by the same team behind ProtonMail, this VPN puts privacy above everything. They publish transparency reports and offer a free tier with no data limits.",
  features: ["Secure Core", "Swiss Privacy Laws", "Open Source Apps"],
  link: "https://vpngenix.com/go/ProtonVPN"
}, {
  name: "Private Internet Access",
  logoIcon: Server,
  badge: "Best for power users",
  description: "Gives you more settings to play with than most providers. Control encryption levels, switch ports, or fine-tune performance. Strong no-logs history.",
  features: ["Advanced Settings", "10 Devices", "MACE Adblocker"],
  link: "https://vpngenix.com/go/PIA"
}, {
  name: "F-Secure",
  logoIcon: ShieldCheck,
  badge: "Best for all-in-one security",
  description: "Protect every screen in your household from web-borne viruses, unsafe download links, and predatory scams through an intuitive interface. F-Secure combines a built-in VPN with real-time malware protection and banking-grade browsing security.",
  features: ["Real-Time Malware Protection", "Banking & Browsing Protection", "Family & Parental Controls"],
  link: "https://vpngenix.com/go/F-Secure"
}, {
  name: "McAfee",
  logoIcon: ShieldAlert,
  badge: "Best for All-in-One Antivirus & Identity",
  description: "Protect your identity and every device with McAfee's comprehensive security suite. Combining real-time antivirus protection, a built-in VPN, and identity monitoring, McAfee keeps your digital life secure from every angle.",
  features: ["Real-Time Antivirus Protection", "Identity Theft Monitoring", "Built-in Password Manager"],
  link: "https://vpngenix.com/go/McAfee"
}];
const criteria = ["Connection speed and stability across global networks", "Device and platform compatibility (Windows, Mac, iOS, Android, Linux)", "Privacy policies, jurisdiction, and strict no-logging practices", "Customer support response times and technical expertise", "Real-world usability for streaming, gaming, and secure browsing", "Pricing value relative to the feature set offered"];
const decisionGuide = [{
  question: "Speed and simplicity?",
  answer: "Go with ExpressVPN or Surfshark.",
  icon: Zap
}, {
  question: "Privacy-first approach?",
  answer: "Proton VPN or PIA are strong bets.",
  icon: Lock
}, {
  question: "Best all-around performance?",
  answer: "NordVPN continues to lead the pack.",
  icon: Shield
}, {
  question: "Need to stay on a strict budget?",
  answer: "FastestVPN covers the basics well.",
  icon: Activity
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function TopRatedVPNComparison() {
  return <section id="topratedvpncomparison" className="bg-background py-24 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        
        <div className="mx-auto max-w-3xl text-center mb-16">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center justify-center rounded-lg bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
            <Crosshair className="mr-2 size-4" />
            Command Center Analysis
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl mb-4 uppercase">
            Top Rated VPNs
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-lg leading-8 text-muted-foreground">
            We tested dozens of services across devices, networks, and use cases. These are the ones that made the cut. They're fast, they protect your data, and they work the way you expect them to.
          </motion.p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-100px"
      }} className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mb-24">
          {providers.map((provider, i) => {
          const Icon = provider.logoIcon;
          return <motion.div key={provider.name} variants={itemVariants} data-index={i} className="flex">
                <Card className="flex flex-col w-full border-2 border-border hover:border-primary transition-colors duration-300 shadow-lg bg-card relative overflow-hidden group">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-md text-primary">
                          <Icon className="size-6" />
                        </div>
                        <CardTitle className="font-default text-xl">{provider.name}</CardTitle>
                      </div>
                    </div>
                    <Badge variant="default" className="w-fit uppercase tracking-wider text-[10px] font-bold">
                      {provider.badge}
                    </Badge>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <p className="text-sm text-muted-foreground mb-6 line-clamp-4">
                      {provider.description}
                    </p>
                    <ul className="space-y-2">
                      {provider.features.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-start text-sm text-foreground">
                          <CheckCircle2 className="mr-2 size-4 text-primary shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                  <CardFooter className="pt-4 border-t border-border/50">
                    <Button asChild className="w-full group/btn">
                      <Link to={provider.link} target="_blank" rel="noopener noreferrer">
                        Visit Site
                        <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-muted/50 rounded-2xl p-8 border border-border">
            <h3 className="font-default text-2xl font-bold text-foreground mb-6 flex items-center">
              <Crosshair className="mr-3 size-6 text-primary" />
              How We Chose These Providers
            </h3>
            <p className="text-muted-foreground mb-8">
              We didn't base this list on affiliate payouts or marketing claims. Instead, we looked at hard data and real-world performance:
            </p>
            <ul className="space-y-4">
              {criteria.map((criterion, i) => <li key={i} data-index={i} className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex size-6 items-center justify-center rounded-lg bg-primary/20 text-primary">
                      <CheckCircle2 className="size-4" />
                    </div>
                  </div>
                  <p className="ml-4 text-foreground">{criterion}</p>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }}>
            <h3 className="font-default text-2xl font-bold text-foreground mb-6 flex items-center">
              <Activity className="mr-3 size-6 text-primary" />
              Which One Is Right for You?
            </h3>
            <p className="text-muted-foreground mb-8">
              All of these providers are solid. The best one depends on what you care about most. Here is a quick guide to help you decide:
            </p>
            <div className="space-y-4">
              {decisionGuide.map((guide, i) => {
              const GuideIcon = guide.icon;
              return <div key={i} data-index={i} className="flex items-start p-4 rounded-lg border border-border bg-card hover:border-primary/50 transition-colors">
                    <div className="flex-shrink-0 mt-1">
                      <GuideIcon className="size-5 text-primary" />
                    </div>
                    <div className="ml-4">
                      <h4 className="text-base font-semibold text-foreground">{guide.question}</h4>
                      <p className="mt-1 text-sm text-muted-foreground">{guide.answer}</p>
                    </div>
                  </div>;
            })}
            </div>
            <div className="mt-8">
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="https://vpngenix.com/go/NordVPN" newTab>
                  Find a VPN For Your Device
                </Link>
              </Button>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}